import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * `showModal()` donde exista. Safari anterior a la 15.4 no tiene `<dialog>`: ahí
 * se abre a mano y la hoja de estilos lo coloca encima de todo
 * (`.modal--sin-dialogo`). Sin esto, abrir cualquier ventana reventaba la
 * pantalla en esos equipos.
 */
export function abrirComoModal(elemento: HTMLDialogElement): void {
  if (typeof elemento.showModal === 'function') {
    elemento.showModal();
    return;
  }
  elemento.setAttribute('open', '');
  elemento.classList.add('modal--sin-dialogo');
}

export function cerrarModal(elemento: HTMLDialogElement): void {
  if (typeof elemento.close === 'function') elemento.close();
  else elemento.removeAttribute('open');
}

interface Props {
  titulo: string;
  onCerrar: () => void;
  children: React.ReactNode;
}

/**
 * Ventana de detalle.
 *
 * Usa el `<dialog>` nativo en vez de una capa hecha a mano: el navegador ya
 * resuelve encerrar el foco dentro, cerrar con Escape y dejar el resto de la
 * página inerte. Reimplementar eso a mano es donde se pierden los detalles de
 * accesibilidad — y siempre se pierde alguno.
 */
export function Modal({ titulo, onCerrar, children }: Props) {
  const { t } = useTranslation();
  const ventana = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const elemento = ventana.current;
    if (!elemento || elemento.open) return;

    abrirComoModal(elemento);
    // El foco al primer campo para escribir, si lo hay. Sin esto el navegador lo
    // deja en «Cerrar», el primer botón que encuentra, y Safari lo enmarca como
    // si fuera lo importante de la ventana. Un selector de archivo no cuenta: va
    // oculto detrás de su botón, y el foco ahí no se vería.
    elemento
      .querySelector<HTMLElement>(
        '.modal__cuerpo input:not([type=hidden]):not([type=checkbox]):not([type=radio]):not([type=file]):not([disabled]), .modal__cuerpo textarea, .modal__cuerpo select',
      )
      ?.focus();
    // El fondo no se desplaza mientras la ventana está abierta: si no, cerrarla
    // devuelve a la persona a otra parte de la lista.
    const desbordeOriginal = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = desbordeOriginal;
      if (elemento.open || elemento.hasAttribute('open')) cerrarModal(elemento);
    };
  }, []);

  return (
    <dialog
      ref={ventana}
      className="modal"
      aria-label={titulo}
      // Escape y el botón de cierre pasan por aquí: el estado de quien abrió la
      // ventana tiene que enterarse, no sólo el elemento.
      onClose={onCerrar}
      onClick={(evento) => {
        // Clic en el fondo. El `<dialog>` ocupa toda la pantalla y su contenido
        // está dentro, así que un clic sobre él mismo es un clic fuera.
        if (evento.target === ventana.current) onCerrar();
      }}
    >
      <div className="modal__encabezado">
        <h2 className="modal__titulo">{titulo}</h2>
        <button className="boton boton--fantasma" type="button" onClick={onCerrar}>
          {t('users.close')}
        </button>
      </div>
      <div className="modal__cuerpo">{children}</div>
    </dialog>
  );
}
